import React from 'react'
import type { Friend, HeatmapWeek, MemberContrib } from '@/types'
import { Avatar } from '@/components/ui/Avatar'

interface HeatmapProps {
  heatmap: HeatmapWeek[]
  contribs: MemberContrib[]
  dataStartedAt: number | null
  startedAt: number | null
  currentSteamId: string
}

function contribToFriend(c: MemberContrib, isMe: boolean): Friend {
  return {
    id: c.steamId,
    name: isMe ? 'You' : c.name,
    handle: c.name,
    status: c.private ? 'limited' : 'public',
    hue: c.hue,
    level: '?',
    avatarUrl: c.avatarUrl,
    nickname: null,
  }
}

export function Heatmap({ heatmap, contribs, dataStartedAt, startedAt, currentSteamId }: HeatmapProps) {
  const weeks = heatmap.length
  const WEEK_SEC = 7 * 86400

  const firstTrackedWeek = startedAt && dataStartedAt
    ? Math.max(0, Math.ceil((dataStartedAt - startedAt) / WEEK_SEC))
    : 0
  const hasGap = firstTrackedWeek > 0

  const totalCoplayWeeks = heatmap.filter((w) => w.togetherActive >= 2).length
  const peakTogether = heatmap.reduce((best, w) => w.togetherActive > best.togetherActive ? w : best, heatmap[0] ?? { togetherActive: 0, weekIdx: 0 })

  return (
    <div className="cg-heatmap">
      <div className="cg-heatmap-header">
        <span className="cg-heatmap-title">Co-play heatmap</span>
        <span className="cg-heatmap-meta">
          {totalCoplayWeeks} {totalCoplayWeeks === 1 ? 'week' : 'weeks'} with co-play · peak {peakTogether.togetherActive}-up
        </span>
      </div>
      {hasGap && (
        <div className="cg-heatmap-gap-note">
          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round">
            <path d="M12 8v4M12 16h.01" /><circle cx="12" cy="12" r="10" />
          </svg>
          <span>Earlier weeks aren't tracked — the group played before this game was on the Playing list.</span>
        </div>
      )}
      <div className="cg-heatmap-legend">
        <span className="cg-heatmap-legend-label">solo</span>
        {[1, 2, 3, 4].map((t) => <span key={t} className="cg-heatmap-legend-swatch" data-tier={String(t)} />)}
        <span className="cg-heatmap-legend-label">full squad</span>
      </div>
      <div
        className="cg-heatmap-grid"
        style={{ gridTemplateColumns: `auto repeat(${weeks}, minmax(0, 1fr))` }}
      >
        {/* Column header row */}
        <div className="cg-heatmap-corner" />
        {heatmap.map((w) => (
          <div
            key={w.weekIdx}
            className="cg-heatmap-col-label"
            data-now={w.weekIdx === weeks - 1 || undefined}
            data-tracked-start={(w.weekIdx === firstTrackedWeek && hasGap) || undefined}
          >
            {w.weekIdx === firstTrackedWeek && hasGap
              ? 'data ▸'
              : w.weekIdx === 0
              ? 'wk 1'
              : w.weekIdx === weeks - 1
              ? 'now'
              : (w.weekIdx + 1) % 4 === 0
              ? String(w.weekIdx + 1)
              : '·'}
          </div>
        ))}

        {/* Together row */}
        <div className="cg-heatmap-row-label cg-heatmap-row-label--together">
          <span className="cg-heatmap-together-dot" />
          <span>Together</span>
        </div>
        {heatmap.map((w) => {
          const noData = w.weekIdx < firstTrackedWeek
          const alpha = noData ? 0 : w.togetherActive === 0 ? 0.08 : 0.25 + Math.min(0.75, w.togetherActive * 0.22)
          const lShift = w.togetherActive >= 3 ? 0.05 : 0
          return (
            <div
              key={w.weekIdx}
              className="cg-heatmap-cell cg-heatmap-cell--together"
              data-active={w.togetherActive}
              data-now={w.weekIdx === weeks - 1 || undefined}
              data-no-data={noData || undefined}
              style={{ '--cell-alpha': alpha, '--cell-l-shift': lShift } as React.CSSProperties}
              title={
                noData ? `Before tracking — week ${w.weekIdx + 1}`
                : w.togetherActive === 0 ? `Quiet week ${w.weekIdx + 1}`
                : w.togetherActive === 1 ? `Solo play, week ${w.weekIdx + 1}`
                : `${w.togetherActive} members co-played, week ${w.weekIdx + 1}`
              }
            >
              {!noData && w.togetherActive >= 2 && (
                <span className="cg-heatmap-count">{w.togetherActive}</span>
              )}
              {noData && <span className="cg-heatmap-nodata">?</span>}
            </div>
          )
        })}

        {/* Member rows */}
        {contribs.map((c) => {
          const isMe = false // currentSteamId check handled by caller if needed
          const f = contribToFriend(c, isMe)
          return (
            <React.Fragment key={c.steamId}>
              <div className="cg-heatmap-row-label">
                <Avatar friend={f} size={18} showRing={false} />
                <span>{c.steamId === currentSteamId ? 'You' : c.name}</span>
              </div>
              {heatmap.map((w) => {
                const cell = w.members[c.steamId]
                if (!cell) return <div key={w.weekIdx} className="cg-heatmap-cell" />
                const noData = w.weekIdx < firstTrackedWeek
                const alpha = cell.private || noData ? 0 : cell.strength > 0 ? 0.15 + cell.strength * 0.7 : 0.06
                return (
                  <div
                    key={w.weekIdx}
                    className="cg-heatmap-cell"
                    data-private={cell.private || undefined}
                    data-no-data={(noData || cell.noData) || undefined}
                    data-active={cell.strength > 0.2 || undefined}
                    data-now={w.weekIdx === weeks - 1 || undefined}
                    style={{ '--cell-alpha': alpha } as React.CSSProperties}
                    title={
                      noData || cell.noData ? `Before tracking — week ${w.weekIdx + 1}`
                      : cell.private ? 'Private profile'
                      : cell.strength > 0 ? `Active week ${w.weekIdx + 1}`
                      : `Quiet week ${w.weekIdx + 1}`
                    }
                  />
                )
              })}
            </React.Fragment>
          )
        })}
      </div>
    </div>
  )
}
